import { createSelector } from '@reduxjs/toolkit';
import { Agent } from '../../../agents/models/Agent';
import { AgentCapabilities } from '../../../agents/models/AgentCapabilities';
import { ChatProviderType } from '../../../chat/models/ChatProviderType';
import { ChatSession } from '../../../chat/models/ChatSession';
import { selectAgentsById } from '../../base/agents/selectAgentsById';
import { selectChatSessionIds } from '../../base/chat/selectChatSessionIds';
import { selectChatSessionsById } from '../../base/chat/selectChatSessionsById';
import { resolveChatSessionCapabilities } from '../../../resolvers/resolveChatSessionCapabilities';
import { resolveSessionAgent } from '../../../resolvers/resolveSessionAgent';
import { selectSelectedSessionId } from '../../base/chat/selectSelectedSessionId';

export interface ChatListItemVm {
    sessionId: string;
    title: string;
    isTitleGenerated?: boolean;
    providerType: ChatProviderType;
    sortTimestamp: number;
    timestampText: string;
    avatarImageName?: string;
    capabilities: AgentCapabilities;
    isSelected: boolean;
}

export interface ChatListViewModel {
    primaryItems: ChatListItemVm[];
    archivedItems: ChatListItemVm[];
}

const toTimestampMs = (session: ChatSession): number => session.modifiedDate ?? session.createdDate ?? 0;

const formatSessionTimestamp = (session: ChatSession): string => {
    const ts = toTimestampMs(session);
    return ts ? new Date(ts).toLocaleString() : 'No activity';
};

const byTimestampDesc = (a: ChatListItemVm, b: ChatListItemVm): number => b.sortTimestamp - a.sortTimestamp;

export const selectChatListViewModel = createSelector(
    [selectChatSessionIds, selectChatSessionsById, selectAgentsById, selectSelectedSessionId],
    (
        sessionIds: string[],
        sessionsById: Record<string, ChatSession>,
        agentsById: Record<string, Agent>,
        selectedSessionId: string | null,
    ): ChatListViewModel => {
        const allItems = sessionIds
            .map((id) => sessionsById[id])
            .map((session): ChatListItemVm => {
                const resolvedAgent = resolveSessionAgent(session, agentsById);
                return {
                    sessionId: session.id,
                    title: session.title,
                    isTitleGenerated: session.isTitleGenerated,
                    providerType: session.providerType,
                    sortTimestamp: toTimestampMs(session),
                    timestampText: formatSessionTimestamp(session),
                    avatarImageName: resolvedAgent?.agentIconUrl,
                    capabilities: resolveChatSessionCapabilities(session, resolvedAgent),
                    isSelected: selectedSessionId === session.id,
                };
            });

        const primaryItems = allItems
            .filter((item) => item.providerType !== ChatProviderType.Assistants)
            .sort(byTimestampDesc);

        const archivedItems = allItems
            .filter((item) => item.providerType === ChatProviderType.Assistants)
            .sort(byTimestampDesc);

        return { primaryItems, archivedItems };
    },
);
